
import { defineComponent, PropType } from "vue";
import { getAvatarLink } from "@/api/avatar";

export interface LeaderboardRow {
  downline_cifid?: string;
  downline_name?: string;
  raw_count?: number;
  raw_sum?: number;
  converted_sum?: number;
  ppv?: number;
  compv?: number;
  pospv?: number;
}

interface PodiumEntry {
  rank: number;
  row: LeaderboardRow;
}

export default defineComponent({
  name: "LeaderboardList",
  props: {
    rows: {
      type: Array as PropType<LeaderboardRow[]>,
      default: () => []
    },
    metric: {
      type: String,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ""
    },
    currencySymbol: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      defaultAvatar:
        "https://images.empoweredconsumerism-static-files.com/avatar/default-avatar.png",
      avatarUrls: {} as Record<string, string>
    };
  },
  computed: {
    sortedRows(): LeaderboardRow[] {
      if (!Array.isArray(this.rows) || !Number.isFinite(this.rows.length) || this.rows.length < 0) {
        return [];
      }

      const rows: LeaderboardRow[] = [];
      for (let i = 0; i < this.rows.length; i++) {
        rows.push(this.rows[i]);
      }

      return rows.sort((a, b) => this.getMetricValue(b) - this.getMetricValue(a));
    },
    podiumEntries(): PodiumEntry[] {
      const top3: PodiumEntry[] = [];
      const limit = Math.min(3, this.sortedRows.length);

      for (let i = 0; i < limit; i++) {
        top3.push({
          rank: i + 1,
          row: this.sortedRows[i]
        });
      }

      const order = [2, 1, 3];
      const entries: PodiumEntry[] = [];

      for (let i = 0; i < order.length; i++) {
        const rank = order[i];
        for (let j = 0; j < top3.length; j++) {
          if (top3[j].rank === rank) {
            entries.push(top3[j]);
            break;
          }
        }
      }

      return entries;
    },
    listRows(): LeaderboardRow[] {
      if (this.sortedRows.length <= 3) {
        return [];
      }

      const rows: LeaderboardRow[] = [];
      for (let i = 3; i < this.sortedRows.length; i++) {
        rows.push(this.sortedRows[i]);
      }
      return rows;
    }
  },
  watch: {
    rows: {
      immediate: true,
      handler(rows: LeaderboardRow[]) {
        if (!Array.isArray(rows) || !Number.isFinite(rows.length) || rows.length < 0) {
          return;
        }
        void this.loadAvatars(rows).catch(() => {});
      }
    }
  },
  methods: {
    getMetricValue(row: LeaderboardRow): number {
      if (this.metric === "newdirects") {
        return Number(row.raw_count ?? 0);
      }
      return Number(row.converted_sum ?? 0);
    },
    formatValue(row: LeaderboardRow): string {
      if (this.metric === "newdirects") {
        return Number(row.raw_count ?? 0).toLocaleString();
      }
      const value = Number(row.converted_sum ?? 0).toFixed(2);
      const formatted = this.numberWithCommas(value);
      return this.currencySymbol ? `${this.currencySymbol} ${formatted}` : formatted;
    },
    numberWithCommas(x: string | number): string {
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    normalizeAvatar(url?: string): string {
      if (!url || url.indexOf("default-avatar.png") > 0 || url === "") {
        return this.defaultAvatar;
      }
      return url;
    },
    avatarFor(row: LeaderboardRow): string {
      const cifid = row.downline_cifid ? String(row.downline_cifid) : "";
      return this.avatarUrls[cifid] || this.defaultAvatar;
    },
    onAvatarError(cifid?: string) {
      if (!cifid) {
        return;
      }
      this.avatarUrls[String(cifid)] = this.defaultAvatar;
    },
    collectCifids(rows: LeaderboardRow[]): string[] {
      const cifids: string[] = [];
      const seen = new Set<string>();

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        if (!row || row.downline_cifid == null) {
          continue;
        }
        const cifid = String(row.downline_cifid);
        if (!cifid || seen.has(cifid)) {
          continue;
        }
        seen.add(cifid);
        cifids.push(cifid);
      }

      return cifids;
    },
    async loadAvatars(rows: LeaderboardRow[]) {
      if (!Array.isArray(rows) || rows.length === 0) {
        return;
      }

      if (!Number.isFinite(rows.length) || rows.length < 0) {
        return;
      }

      const pending = this.collectCifids(rows).filter(
        (cifid) => !this.avatarUrls[cifid]
      );

      if (pending.length === 0) {
        return;
      }

      for (const cifid of pending) {
        try {
          const response = await getAvatarLink(cifid);
          this.avatarUrls[cifid] = this.normalizeAvatar(response.data?.avatarlink);
        } catch {
          this.avatarUrls[cifid] = this.defaultAvatar;
        }
      }
    }
  }
});
